// The paywall (components/modals/UpgradeModal), the locked cover
// (components/studio/LockedCover, LockedVideoPreview) and the bar that
// brings people back to a checkout they left (components/upsell/ReturnBar).
// See messages/README.md for how to add strings.
//
// Prices and plan names arrive already formatted as {price} / {plan}; only
// the sentence around them is translated. {time} is a live countdown
// ("47:12:03") that the code styles, so it stays a placeholder.
export const paywall = {
  en: {
    // What each plan includes (the "What you get with <plan>" list).
    "paywall.hl.unlimitedCovers": "Unlimited covers",
    "paywall.hl.voices": "60,000+ voices",
    "paywall.hl.downloads": "Downloads",
    "paywall.hl.duetsTts": "Duets and text to speech",
    "paywall.hl.trainVoices": "Train your voices",
    "paywall.hl.hdVideos": "1080p videos",
    "paywall.hl.videoCoins": "50 video coins a month",
    "paywall.hl.marbleLyric": "Marble Run and lyric videos",
    "paywall.hl.priority": "Priority processing",
    "paywall.hl.unlimitedCoins": "Unlimited video coins",
    "paywall.hl.apiCredits": "$25 API credits a month",
    "paywall.hl.everythingCreator": "Everything in Creator",
    "paywall.hl.thisCover": "This cover, now",

    // Why the paywall opened: headline and line under it.
    "paywall.reason.unlock.title": "Unlock your cover",
    "paywall.reason.unlock.body":
      "Your voice and audio are set. Subscribe and your cover starts straight away, ready in about a minute. Then make as many as you like.",
    "paywall.reason.create.title": "Your cover is ready to make",
    "paywall.reason.create.body":
      "Your audio and voice are set up. Pick a plan to make it, then make as many covers as you like.",
    "paywall.reason.browse.title": "Make covers with any voice",
    "paywall.reason.browse.body":
      "Unlimited covers, downloads and all 60,000+ voices. Cancel any time.",
    "paywall.reason.credits.title": "You've used your credits",
    "paywall.reason.credits.body":
      "Your next cover is waiting. Basic has no limit, so you never count credits again.",
    "paywall.reason.words.title": "Make the voice say your words",
    "paywall.reason.words.body":
      "Type words or describe a song and the voice sings or says it. Included from Basic.",
    "paywall.reason.video.title": "Turn it into a video",
    "paywall.reason.video.body":
      "Marble Run and lyric videos, ready for TikTok and Reels. Creator includes 50 video coins every month.",
    "paywall.reason.resume.title": "Pick up where you left off",
    "paywall.reason.resume.body":
      "Your plan is saved. Finish checking out, then make as many covers as you like.",
    "paywall.reason.train.title": "Make a voice nobody else has",
    "paywall.reason.train.body":
      "Train your own voices from a few samples and use them in any cover. Unlimited on Creator.",

    // The main button.
    "paywall.cta.unlockFor": "Unlock my cover for {price}",
    "paywall.cta.keepGoingFor": "Keep going for {price}",
    "paywall.cta.backToCreatorFor": "Come back to Creator for {price}",
    "paywall.cta.backFor": "Come back for {price}",
    "paywall.cta.startFor": "Start for {price}",
    "paywall.cta.startPlanFor": "Start {plan} for {price}",

    // Renewal terms next to the pay button (required before payment).
    "paywall.renew.welcome":
      "{price} a month for as long as you stay. Renews monthly until you cancel.",
    "paywall.renew.then":
      "Then {price} a month. Renews monthly until you cancel.",
    "paywall.renew.yearly":
      "{price} billed today for 12 months. Renews yearly until you cancel.",

    "paywall.toast.updated": "Your subscription has been updated!",
    "paywall.toast.error": "Something went wrong, please try again.",

    // Welcome back (their paid plan ended).
    "paywall.welcomeBack": "WELCOME BACK",
    "paywall.priceMonth": "{price} a month",
    "paywall.welcome.offerCreator": "Creator for {price} a month",
    "paywall.welcome.card":
      "Your plan stopped because a payment didn't go through, not because you left. Pick up where you left off for {offer}.",
    "paywall.welcome.price":
      "You told us it cost too much, so here's less: {offer} instead of {was}, for as long as you stay.",
    "paywall.welcome.broken":
      "You told us something wasn't working. In the last week {n} in 100 covers came out fine. Come back for {offer}.",
    "paywall.welcome.secretCreator":
      "Creator for {price} a month on a plan we don't advertise. It stays yours for as long as you're subscribed.",
    "paywall.welcome.secret":
      "Everything you had, for {price} a month on a plan we don't advertise. It stays yours for as long as you're subscribed.",
    "paywall.welcome.coversHere": "Your {n} covers are still here",
    "paywall.welcome.coverHere": "Your cover is still here",
    "paywall.welcome.makeAgain": "Make covers again",
    "paywall.ended.today": "Your plan ended today",
    "paywall.ended.yesterday": "Your plan ended yesterday",
    "paywall.ended.days": "Your plan ended {n} days ago",
    "paywall.ended.months": "Your plan ended {n} months ago",
    "paywall.more": "more",

    // Plan cards and the hero.
    "paywall.recommended": "RECOMMENDED",
    "paywall.perMonth": "a month",
    "paywall.perMonthStay": "a month, for as long as you stay",
    "paywall.firstMonth": "first month",
    "paywall.today": "today",
    "paywall.heldFor": "Held for {time}",
    "paywall.coverReady": "YOUR COVER IS READY TO MAKE",
    "paywall.coverSetUp": "Your audio and voice are set up",
    "paywall.coversToday": "{n} covers made on Jammable today",
    "paywall.coversTodayShort": "{n} covers made today",
    "paywall.pickPlanUnlock": "Pick a plan to unlock it",
    "paywall.pickPlan": "Pick a plan",
    "paywall.whatYouGet": "WHAT YOU GET WITH {plan}",
    "paywall.secure": "Secure checkout with Stripe · cancel any time",
    "paywall.topPlan": "You're on our top plan",
    "paywall.managePlan": "Manage your plan",

    // Checkout step.
    "paywall.changePlan": "Change plan",
    "paywall.planYearly": "{plan}, yearly",
    "paywall.planMonthly": "{plan} plan",
    "paywall.billedYearly": "Billed yearly",
    "paywall.pay": "Pay",
    "paywall.payUnlock": "Pay and unlock",
    "paywall.checkoutLoading": "Getting your checkout ready",

    // Leaving without the cover.
    "paywall.leave.title": "Leave without your cover?",
    "paywall.leave.oneStep": "You're one step away.",
    "paywall.leave.audioIn": "Your audio is in",
    "paywall.leave.voiceReady": "{names} is ready to sing it",
    "paywall.leave.voicesReady": "{names} are ready to sing it",
    "paywall.leave.voicePicked": "Your voice is picked",
    "paywall.leave.makeStep": "Make the cover: about a minute",
    "paywall.leave.gets": "UNLOCKING GETS YOU",
    "paywall.leave.held":
      "It's held for {time}. After that it's gone and you'd have to make it again.",
    "paywall.unlockMyCover": "Unlock my cover",
    "paywall.leaveAnyway": "Leave anyway",

    // The yearly gift on the leave step.
    "paywall.gift.wrapping": "Wrapping something for you…",
    "paywall.gift.beforeYouGo": "BEFORE YOU GO",
    "paywall.gift.saved": "We've saved you a gift",
    "paywall.gift.openToSee": "Open it to see what it is.",
    "paywall.gift.opening": "Opening…",
    "paywall.gift.open": "Open my gift",
    "paywall.gift.noThanks": "No thanks, leave",
    "paywall.gift.yours": "YOUR GIFT",
    "paywall.gift.headline": "{plan} for {price} a month",
    "paywall.gift.body":
      "Unlimited covers with every voice, locked in for 12 months.",
    "paywall.gift.save": "SAVE {pct}%",
    "paywall.perMo": "/mo",
    "paywall.gift.billedYearly": "{price} billed yearly",
    "paywall.gift.lockIn": "Lock in {price} a month",
    "paywall.seeMonthly": "See monthly plans",

    // The locked cover's page.
    "paywall.locked.step.upload": "Uploading your audio",
    "paywall.locked.step.match": "Matching the voice",
    "paywall.locked.step.mix": "Setting up the mix",
    "paywall.locked.yourAudio": "your audio",
    "paywall.locked.yourAiSong": "your AI song",
    "paywall.locked.yourWords": "your words",
    "paywall.locked.yourVoice": "Your voice",
    "paywall.locked.startFailed": "Couldn't start the cover. Try again.",
    "paywall.locked.makeNow": "Make it now",
    "paywall.unlock": "Unlock",
    "paywall.locked.pctDone": "{pct}% done",
    "paywall.locked.gettingReady": "Getting {names} ready for {song}",
    "paywall.locked.done": "done",
    "paywall.locked.now": "now",
    "paywall.locked.expired": "This cover expired",
    "paywall.locked.expiredBody":
      "Locked covers are held for 48 hours. Make it again in a minute.",
    "paywall.locked.makeAgain": "Make it again",
    "paywall.locked.readyToMake": "Ready to make",
    "paywall.locked.readyOne": "{names} AI voice is ready to voice {song}",
    "paywall.locked.readyMany": "{names} AI voices are ready to voice {song}",
    "paywall.locked.onPlan":
      "You're on a plan, so it can start right now. It takes about a minute.",
    "paywall.locked.unlockBody":
      "Unlock it and your cover starts straight away. It takes about a minute, and then it's yours to download and share.",
    "paywall.locked.change": "Change the voice or audio",
    "paywall.locked.notMade": "This cover hasn't been made yet",
    "paywall.locked.makeYourOwn":
      "Make your own with {names} in about a minute.",
    "paywall.locked.makeCover": "Make a cover",

    // The locked video preview. "\n" is a line break.
    "paywall.video.unlock": "Unlock your cover\nand video",
    "paywall.video.howItLooks": "This is how your video will look",
    "paywall.video.drawing": "Drawing your video",
    "paywall.video.aria": "{cta}: your cover and its video",

    // The return bar.
    "paywall.return.firstMonth": "{price} for your first month",
    "paywall.return.waiting": "Your {title} cover is waiting",
    "paywall.return.savedWaiting": "Your saved cover is waiting",
    "paywall.return.trialReady": "Your 7-day free trial is ready",
    "paywall.return.trialDetail":
      "Nothing to pay today, then {price} a month. Cancel any time.",
    "paywall.return.startTrial": "Start my free trial",
    "paywall.return.funds": "Your bank said there wasn't enough on the card.",
    "paywall.return.declined": "Your bank declined it.",
    "paywall.return.cardFailed": "Your card didn't go through",
    "paywall.return.cardDetail":
      "Your card didn't go through. Try Apple Pay, Google Pay or another card · {price}",
    "paywall.return.whyDetail":
      "{why} Try Apple Pay, Google Pay or another card · {price}",
    "paywall.return.tryAnother": "Try another way",
    "paywall.return.confirm": "Your bank wanted a quick confirmation",
    "paywall.return.confirmDetail": "One more step and you're in · {price}",
    "paywall.return.finish": "Finish paying",
    "paywall.return.planHere": "Your {plan} plan is still here",
    "paywall.return.heldMore": "Held for {time} more · {price}",
    "paywall.return.thenRegular": "{price}, then {regular}",
    "paywall.return.cancelAny": "{price}, cancel any time",
    "paywall.return.pickUp": "Pick up where I left off",
    "paywall.return.cardStopped":
      "Your plan stopped when a payment didn't go through",
    "paywall.return.comeBack": "Come back to Jammable",
    "paywall.return.pickUpFor": "Pick up where you left off for {offer}.",
    "paywall.return.secretOffer":
      "Come back for {offer}, on a plan we don't advertise.",
    "paywall.return.hide": "Hide",
  },
};
